import { styled } from '@mui/material/styles';
import { 
  Dialog, 
  DialogTitle as MuiDialogTitle, 
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  TextField,
  Box,
  Button,
  Tab as MuiTab,
  Tabs,
  Chip as MuiChip,
  Card as MuiCard
} from '@mui/material';

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#1e1e1e',
    color: theme.palette.text.primary,
    borderRadius: theme.spacing(1),
    minWidth: '700px',
    maxWidth: '90vw',
    maxHeight: '85vh'
  },
}));

export const DialogHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#333',
  padding: theme.spacing(2),
  position: 'relative',
}));

export const DialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  padding: 0,
  color: 'white',
  fontSize: '1.25rem',
  fontWeight: 600,
}));

export const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
  padding: theme.spacing(3, 3),
  backgroundColor: '#1e1e1e',
  maxHeight: '70vh',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 0, 0, 0.1)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(255, 255, 255, 0.3)',
  },
}));

export const DialogActions = styled(MuiDialogActions)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#1e1e1e',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
}));

export const TabsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#333',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}));

export const TabsList = styled(Tabs)(({ theme }) => ({
  minHeight: '48px',
  
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
  },
}));

interface TabProps {
  active?: boolean;
}

export const Tab = styled(MuiTab, {
  shouldForwardProp: (prop) => prop !== 'active'
})<TabProps>(({ theme, active }) => ({
  textTransform: 'none',
  minWidth: 0,
  minHeight: '48px',
  padding: theme.spacing(0, 2),
  color: active ? 'white' : 'rgba(255, 255, 255, 0.7)',
  fontWeight: active ? 600 : 400,
  backgroundColor: active ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
  
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
  },
})) as React.ComponentType<TabProps & React.ComponentProps<typeof MuiTab>>;

export const FormGroup = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
}));

export const FormLabel = styled(Box)(({ theme }) => ({
  fontSize: '0.9rem',
  fontWeight: 500,
  marginBottom: theme.spacing(0.75),
  color: 'rgba(255, 255, 255, 0.7)',
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: theme.spacing(0.75),
    
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
  },
  
  '& .MuiFormHelperText-root': {
    color: theme.palette.error.main,
  },
}));

export const OptionsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

export const OptionCard = styled(MuiCard, {
  shouldForwardProp: (prop) => prop !== 'selected'
})<{ selected?: boolean }>(({ theme, selected }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(0.75),
  backgroundColor: selected ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.2)',
  border: `1px solid ${selected ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.1)'}`,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  
  '&:hover': {
    backgroundColor: selected ? 'rgba(25, 118, 210, 0.25)' : 'rgba(0, 0, 0, 0.3)',
    borderColor: selected ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.2)',
  },
}));

export const OptionIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const OptionTitle = styled(Box)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '0.9rem',
  color: theme.palette.text.primary,
}));

export const OptionDescription = styled(Box)(({ theme }) => ({
  fontSize: '0.8rem',
  color: 'rgba(255, 255, 255, 0.5)',
}));

export const DatePickerField = styled(Box)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: theme.spacing(0.75),
    
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
  },
}));

export const CategoryChip = styled(MuiChip, {
  shouldForwardProp: (prop) => prop !== '$color' && prop !== 'selected'
})<{ $color: string; selected?: boolean }>(({ theme, $color, selected }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: selected ? $color : 'transparent',
  color: selected ? '#fff' : $color,
  border: `1px solid ${$color}`,
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  
  '&:hover': {
    backgroundColor: selected ? $color : `${$color}22`,
  },
  
  '& .MuiChip-icon': {
    color: 'inherit',
  },
}));

export const CancelButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(0.75),
  color: 'rgba(255, 255, 255, 0.7)',
  
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
}));

export const SaveButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(0.75),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  
  '&:disabled': {
    backgroundColor: 'rgba(25, 118, 210, 0.3)',
    color: 'rgba(255, 255, 255, 0.3)',
  },
}));

export const DeleteButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(0.75),
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
}));

export const AttachmentsList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
}));

export const CommentsList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
}));

export const CommentItem = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  padding: theme.spacing(1),
  borderRadius: theme.spacing(0.75),
  border: '1px solid rgba(255, 255, 255, 0.1)',
  width: '100%',
}));

export const CommentHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(0.5),
  gap: theme.spacing(1),
}));

export const CommentAuthor = styled(Box)(({ theme }) => ({
  fontWeight: 500,
}));

export const CommentDate = styled(Box)(({ theme }) => ({
  fontSize: '0.8rem',
  color: 'rgba(255, 255, 255, 0.5)',
}));

export const CommentText = styled(Box)(({ theme }) => ({
  color: theme.palette.text.primary,
  wordBreak: 'break-word',
  overflow: 'auto',
  maxHeight: '100px',
  fontSize: '0.9rem',
}));

export const AttachmentItem = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  padding: theme.spacing(0.75),
  borderRadius: theme.spacing(0.75),
  border: '1px solid rgba(255, 255, 255, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

export const AttachmentInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const AttachmentName = styled(Box)(({ theme }) => ({
  fontWeight: 500,
}));

export const AttachmentSize = styled(Box)(({ theme }) => ({
  fontSize: '0.8rem',
  color: 'rgba(255, 255, 255, 0.5)',
}));

export const FileUploadContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(0.75),
  border: '1px dashed rgba(255, 255, 255, 0.3)',
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  cursor: 'pointer',
  
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderColor: 'rgba(255, 255, 255, 0.5)',
  },
}));
